import { z } from "zod";
export function deriveBlockLifecycle(input) {
    const { totalItems, captureStatus, resultRowCount, verdictRowCount } = input;
    // No capture row + no results → supervisor hasn't opened the bloque.
    if (captureStatus === undefined && resultRowCount === 0) {
        return "pendiente";
    }
    // analysis_failed and pending both surface as "capturando" to the
    // supervisor — the FE renders the retry affordance based on the
    // captureStatus directly when needed.
    if (captureStatus === "pending" || captureStatus === "analysis_failed") {
        return "capturando";
    }
    // Analyzing capture + no result rows yet → the AI job is running.
    if (captureStatus === "analyzing") {
        return "procesando_ia";
    }
    // Analyzed capture: either revisión in flight or fully closed.
    if (captureStatus === "analyzed") {
        if (totalItems > 0 && verdictRowCount >= totalItems) {
            return "cerrado";
        }
        return "revisando";
    }
    // Defensive fallback: any other shape (capture undefined but result
    // rows present, etc.) reads as capturando — the supervisor's "next
    // action" surface is the same.
    return "capturando";
}
// ─── Per-item evidence requirement (ADR-0088) ──────────────────────────
//
// Configurable per inspection_checklist_items row, not per recipe: the
// facility checklist mixes items that want a photo of a condition with
// items that want a dictated observation, so a single vertical-wide
// switch can't express it. "Off for this recipe" is the vacuous case
// where every item is 'none' — there is deliberately no separate
// recipe-level primitive (see ADR-0088 §Decision / §Options B).
//
// Values read as a MINIMUM, not an exclusive list: 'photo' means "a
// photo, at minimum" not "only a photo is allowed" — nothing stops a
// supervisor from also recording a voice note on a photo-required item.
export const evidenceRequirementSchema = z.enum([
    "none",
    "photo",
    "audio",
    "photo_and_audio",
    "photo_or_audio",
]);
/** Pure predicate: does this item's captured evidence satisfy its own
 *  evidenceRequirement? The single implementation — both the server-side
 *  gate (POST .../blocks/:areaId/complete) and the read-contract's
 *  per-item "done" flag (GET .../blocks/:areaId/items) call this rather
 *  than each re-deriving it, so the two surfaces cannot drift apart. */
export function itemEvidenceSatisfied(requirement, counts) {
    const { photoCount, noteCount } = counts;
    switch (requirement) {
        case "none":
            return true;
        case "photo":
            return photoCount > 0;
        case "audio":
            return noteCount > 0;
        case "photo_and_audio":
            return photoCount > 0 && noteCount > 0;
        case "photo_or_audio":
            return photoCount > 0 || noteCount > 0;
        default:
            // Defensive: reachable only if a value outside evidenceRequirementSchema
            // reaches here via an unchecked `as EvidenceRequirement` cast (e.g. a
            // raw DB read) rather than through the schema. Fail closed — an
            // unrecognized requirement is treated as the strictest, never as
            // satisfied.
            return false;
    }
}
/** seccionCerrable — every item flagged is_required=true must satisfy
 *  its own evidenceRequirement. An item the supervisor may skip entirely
 *  cannot coherently demand evidence, so non-required items are excluded
 *  from the check regardless of their evidenceRequirement value. Empty
 *  input (no items in the bloque, or none required) is vacuously true —
 *  callers that want "at least one item exists" as a separate precondition
 *  (e.g. the FE's empty-section state) must check totalItems themselves;
 *  this function only answers the evidence question. */
export function deriveSectionClosable(items) {
    return items
        .filter((i) => i.isRequired)
        .every((i) => itemEvidenceSatisfied(i.evidenceRequirement, i.counts));
}
/** The itemIds that block closure — the enumeration the 409
 *  SECTION_EVIDENCE_INCOMPLETE error and the FE's gate hint both need.
 *  Same required-only filter as deriveSectionClosable; kept as a
 *  separate function rather than a mode flag so each call site's intent
 *  (boolean gate vs. detail list) is explicit at the call site. */
export function missingEvidenceItems(items) {
    return items
        .filter((i) => i.isRequired)
        .filter((i) => !itemEvidenceSatisfied(i.evidenceRequirement, i.counts));
}
//# sourceMappingURL=blocks.js.map